import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useState } from 'react';
import axios from 'axios';
import { Stamp, Ship, FileCheck, CreditCard, KeyRound, CheckCircle, Phone, ArrowRight, ChevronDown, ChevronUp, Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServiceCard = ({ id, icon: Icon, title, desc, time }) => (
  <a href={`#${id}`} className="bg-navy-800 border border-white/5 rounded-2xl p-6 card-hover group block">
    <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-4 group-hover:bg-blue-500/20">
      <Icon className="w-6 h-6 text-blue-400" />
    </div>
    <h3 className="font-heading font-semibold text-lg text-white mb-2">{title}</h3>
    <p className="text-slate-400 text-sm mb-3">{desc}</p>
    <div className="text-blue-400 text-sm font-medium">{time}</div>
  </a>
);

const ServiceSection = ({ id, icon: Icon, title, short, desc, benefits, docs, time, req, alt }) => (
  <section id={id} className={`py-16 md:py-20 ${alt ? 'bg-navy-800/50' : ''}`}>
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
              <Icon className="w-6 h-6 text-blue-400" />
            </div>
            <span className="text-blue-400 font-medium">{time}</span>
          </div>
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4">{title}</h2>
          <p className="text-slate-400 mb-6">{desc}</p>
          <h4 className="font-heading font-semibold text-white mb-3">Key Benefits</h4>
          <ul className="space-y-2 mb-6">
            <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" /><span className="text-slate-300">{benefits[0]}</span></li>
            <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" /><span className="text-slate-300">{benefits[1]}</span></li>
            <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" /><span className="text-slate-300">{benefits[2]}</span></li>
            <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" /><span className="text-slate-300">{benefits[3]}</span></li>
          </ul>
          <div className="bg-navy-800 border border-white/5 rounded-xl p-4 inline-block">
            <span className="text-slate-400 text-sm">Validity: </span>
            <span className="text-white font-medium">{req}</span>
          </div>
        </div>
        <div className="bg-navy-800 border border-white/5 rounded-2xl p-8">
          <h4 className="font-heading font-semibold text-xl text-white mb-6">Documents Required</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-start gap-3"><div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0"><CheckCircle className="w-4 h-4 text-blue-400" /></div><span className="text-slate-300 text-sm">{docs[0]}</span></div>
            <div className="flex items-start gap-3"><div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0"><CheckCircle className="w-4 h-4 text-blue-400" /></div><span className="text-slate-300 text-sm">{docs[1]}</span></div>
            <div className="flex items-start gap-3"><div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0"><CheckCircle className="w-4 h-4 text-blue-400" /></div><span className="text-slate-300 text-sm">{docs[2]}</span></div>
            {docs[3] && <div className="flex items-start gap-3"><div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0"><CheckCircle className="w-4 h-4 text-blue-400" /></div><span className="text-slate-300 text-sm">{docs[3]}</span></div>}
            {docs[4] && <div className="flex items-start gap-3"><div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0"><CheckCircle className="w-4 h-4 text-blue-400" /></div><span className="text-slate-300 text-sm">{docs[4]}</span></div>}
          </div>
          <div className="mt-6 pt-6 border-t border-white/10">
            <a href="#contact-form"><Button className="w-full bg-blue-500 hover:bg-blue-600 text-white">Get {short}<ArrowRight className="ml-2 w-4 h-4" /></Button></a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const OtherServicesPage = () => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', service: 'Trademark Registration' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [faq, setFaq] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/contact`, { ...form, service_type: form.service, message: `Request for ${form.service}`, source_page: 'Other Services' });
      setSubmitted(true);
    } catch (err) { console.error(err); }
    setSubmitting(false);
  };

  return (
    <div data-testid="other-services-page" className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0"><div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" /></div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                <span className="text-slate-400 text-sm">4.9 (800+ ratings)</span>
              </div>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white">Other Registration <span className="text-gradient">Services</span></h1>
              <p className="text-lg text-slate-400">Get Trademark, IEC, LUT, TAN registrations and Digital Certificates with expert assistance.</p>
              <div className="flex flex-wrap gap-4">
                <span className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-5 h-5 text-blue-400" />Expert Support</span>
                <span className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-5 h-5 text-blue-400" />Quick Processing</span>
              </div>
              <a href="tel:+919717705640"><Button className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6 text-lg"><Phone className="mr-2 w-5 h-5" />Call: +91-9717705640</Button></a>
            </div>
            <div id="contact-form" className="bg-navy-800 border border-white/10 rounded-2xl p-8">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mb-4"><CheckCircle className="w-8 h-8 text-green-400" /></div>
                  <h3 className="font-heading font-bold text-xl text-white mb-2">Request Received!</h3>
                  <p className="text-slate-400">Our expert will call you shortly.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <h3 className="font-heading font-bold text-xl text-white mb-6">Get Free Consultation</h3>
                  <div><Label className="text-slate-300">Your Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required placeholder="Enter your name" className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500" /></div>
                  <div><Label className="text-slate-300">Contact Number</Label><Input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required placeholder="+91 XXXXX XXXXX" className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500" /></div>
                  <div><Label className="text-slate-300">Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required placeholder="your@email.com" className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500" /></div>
                  <div><Label className="text-slate-300">Service Type</Label>
                    <select value={form.service} onChange={e => setForm({...form, service: e.target.value})} className="w-full h-10 px-3 rounded-md bg-navy-900 border border-white/10 text-white">
                      <option value="Trademark Registration">Trademark Registration</option>
                      <option value="IEC Registration">IEC Registration</option>
                      <option value="LUT Registration">LUT Registration</option>
                      <option value="TAN Registration">TAN Registration</option>
                      <option value="Digital Certificate">Digital Certificate</option>
                    </select>
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full bg-blue-500 hover:bg-blue-600 text-white py-6">{submitting ? 'Submitting...' : 'Get Free Consultation'}</Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24 bg-navy-800/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12"><span className="text-blue-400 font-medium mb-4 block">Our Services</span><h2 className="font-heading font-bold text-3xl md:text-4xl text-white">Registration & Certification Services</h2></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            <ServiceCard id="trademark" icon={Stamp} title="Trademark" desc="Protect your brand identity" time="4-6 months" />
            <ServiceCard id="iec" icon={Ship} title="IEC Code" desc="Import Export License" time="3-5 days" />
            <ServiceCard id="lut" icon={FileCheck} title="LUT" desc="Letter of Undertaking" time="1-2 days" />
            <ServiceCard id="tan" icon={CreditCard} title="TAN" desc="Tax Deduction Number" time="7-10 days" />
            <ServiceCard id="dsc" icon={KeyRound} title="DSC" desc="Digital Signature" time="1-2 days" />
          </div>
        </div>
      </section>

      {/* Service Sections */}
      <ServiceSection 
        id="trademark" 
        icon={Stamp} 
        title="Trademark Registration" 
        short="Trademark" 
        desc="Protect your brand name, logo, and tagline with trademark registration. Gives you exclusive rights to use your brand identity." 
        benefits={['Exclusive brand ownership', 'Legal protection against copying', 'Builds brand value & trust', 'Valid for 10 years (renewable)']} 
        docs={['Brand/Logo copy', 'ID Proof of applicant', 'Address Proof', 'Business registration proof', 'User affidavit (if used)']} 
        time="4-6 months" 
        req="10 Years (Renewable)" 
        alt={false} 
      />
      <ServiceSection 
        id="iec" 
        icon={Ship} 
        title="IEC Registration (Import Export Code)" 
        short="IEC Code" 
        desc="IEC is mandatory for importing or exporting goods and services from India. Get your IEC code from DGFT quickly." 
        benefits={['Required for all import/export', 'Lifetime validity (no renewal)', 'Access to export benefits', 'Easy online application']} 
        docs={['PAN Card', 'Aadhaar Card', 'Cancelled Cheque', 'Business registration', 'Address proof of office']} 
        time="3-5 days" 
        req="Lifetime (No Renewal)" 
        alt={true} 
      />
      <ServiceSection 
        id="lut" 
        icon={FileCheck} 
        title="LUT Registration (Letter of Undertaking)" 
        short="LUT" 
        desc="LUT allows GST-registered exporters to export goods/services without paying IGST. File LUT annually for zero-rated exports." 
        benefits={['Export without paying IGST', 'Improves cash flow', 'Simple annual filing', 'Valid for entire financial year']} 
        docs={['GST Registration', 'PAN Card', 'IEC Code (for goods)', 'Bank details', 'Previous LUT (if any)']} 
        time="1-2 days" 
        req="1 Year (Annual Filing)" 
        alt={false} 
      />
      <ServiceSection 
        id="tan" 
        icon={CreditCard} 
        title="TAN Registration (Tax Deduction Account Number)" 
        short="TAN" 
        desc="TAN is mandatory for anyone responsible for deducting or collecting tax at source (TDS/TCS). Apply online for TAN." 
        benefits={['Required for TDS deduction', 'Mandatory for businesses', 'Used for TDS returns filing', 'Quick online process']} 
        docs={['PAN Card', 'Aadhaar Card', 'Address Proof', 'Business registration']} 
        time="7-10 days" 
        req="Lifetime (No Renewal)" 
        alt={true} 
      />
      <ServiceSection 
        id="dsc" 
        icon={KeyRound} 
        title="Digital Signature Certificate (DSC)" 
        short="DSC" 
        desc="DSC is the digital equivalent of physical signatures. Required for company registration, GST, ITR filing, and other online filings." 
        benefits={['Legally valid e-signature', 'Required for MCA filings', 'Secure document signing', 'Class 3 DSC available']} 
        docs={['PAN Card', 'Aadhaar Card', 'Passport photo', 'Video verification']} 
        time="1-2 days" 
        req="1-2 Years (as per class)" 
        alt={false} 
      />

      {/* Comparison Table */}
      <section className="py-16 md:py-24 bg-navy-800/50">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12"><span className="text-blue-400 font-medium mb-4 block">Comparison</span><h2 className="font-heading font-bold text-3xl md:text-4xl text-white">Quick Overview</h2></div>
          <div className="bg-navy-800 border border-white/5 rounded-2xl overflow-x-auto">
            <table className="w-full min-w-[500px]">
              <thead><tr className="bg-navy-900/50 border-b border-white/5"><th className="px-4 py-4 text-left font-heading font-semibold text-white">Service</th><th className="px-4 py-4 text-left font-heading font-semibold text-white">Processing</th><th className="px-4 py-4 text-left font-heading font-semibold text-white">Validity</th></tr></thead>
              <tbody>
                <tr className="border-b border-white/5"><td className="px-4 py-4 text-blue-400 font-medium">Trademark</td><td className="px-4 py-4 text-slate-300">4-6 months</td><td className="px-4 py-4 text-slate-300">10 Years</td></tr>
                <tr className="border-b border-white/5"><td className="px-4 py-4 text-blue-400 font-medium">IEC Code</td><td className="px-4 py-4 text-slate-300">3-5 days</td><td className="px-4 py-4 text-slate-300">Lifetime</td></tr>
                <tr className="border-b border-white/5"><td className="px-4 py-4 text-blue-400 font-medium">LUT</td><td className="px-4 py-4 text-slate-300">1-2 days</td><td className="px-4 py-4 text-slate-300">1 Year</td></tr>
                <tr className="border-b border-white/5"><td className="px-4 py-4 text-blue-400 font-medium">TAN</td><td className="px-4 py-4 text-slate-300">7-10 days</td><td className="px-4 py-4 text-slate-300">Lifetime</td></tr>
                <tr><td className="px-4 py-4 text-blue-400 font-medium">DSC</td><td className="px-4 py-4 text-slate-300">1-2 days</td><td className="px-4 py-4 text-slate-300">1-2 Years</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12"><span className="text-blue-400 font-medium mb-4 block">FAQs</span><h2 className="font-heading font-bold text-3xl md:text-4xl text-white">Frequently Asked Questions</h2></div>
          <div className="space-y-4">
            <div className="bg-navy-800 border border-white/5 rounded-xl overflow-hidden">
              <button onClick={() => setFaq(faq === 0 ? null : 0)} className="w-full px-6 py-5 flex items-center justify-between text-left">
                <span className="font-heading font-semibold text-white">What is the difference between TM and R symbol?</span>
                {faq === 0 ? <ChevronUp className="w-5 h-5 text-blue-400" /> : <ChevronDown className="w-5 h-5 text-blue-400" />}
              </button>
              {faq === 0 && <div className="px-6 pb-5"><p className="text-slate-400">TM symbol can be used once trademark application is filed. R symbol can only be used after trademark is registered.</p></div>}
            </div>
            <div className="bg-navy-800 border border-white/5 rounded-xl overflow-hidden">
              <button onClick={() => setFaq(faq === 1 ? null : 1)} className="w-full px-6 py-5 flex items-center justify-between text-left">
                <span className="font-heading font-semibold text-white">Is IEC required for service exports?</span>
                {faq === 1 ? <ChevronUp className="w-5 h-5 text-blue-400" /> : <ChevronDown className="w-5 h-5 text-blue-400" />}
              </button>
              {faq === 1 && <div className="px-6 pb-5"><p className="text-slate-400">IEC is mandatory for goods export. For service exports, IEC is not mandatory but LUT is required for GST exemption.</p></div>}
            </div>
            <div className="bg-navy-800 border border-white/5 rounded-xl overflow-hidden">
              <button onClick={() => setFaq(faq === 2 ? null : 2)} className="w-full px-6 py-5 flex items-center justify-between text-left">
                <span className="font-heading font-semibold text-white">What is the validity of Digital Signature?</span>
                {faq === 2 ? <ChevronUp className="w-5 h-5 text-blue-400" /> : <ChevronDown className="w-5 h-5 text-blue-400" />}
              </button>
              {faq === 2 && <div className="px-6 pb-5"><p className="text-slate-400">DSC is typically valid for 1-2 years based on the certificate class. Class 3 DSC for MCA filings is usually valid for 2 years.</p></div>}
            </div>
            <div className="bg-navy-800 border border-white/5 rounded-xl overflow-hidden">
              <button onClick={() => setFaq(faq === 3 ? null : 3)} className="w-full px-6 py-5 flex items-center justify-between text-left">
                <span className="font-heading font-semibold text-white">Who needs TAN registration?</span>
                {faq === 3 ? <ChevronUp className="w-5 h-5 text-blue-400" /> : <ChevronDown className="w-5 h-5 text-blue-400" />}
              </button>
              {faq === 3 && <div className="px-6 pb-5"><p className="text-slate-400">Any person or entity responsible for deducting TDS (like employers, businesses making payments) must obtain TAN.</p></div>}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-navy-800/50">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="bg-gradient-to-r from-blue-500/10 to-sky-500/10 border border-white/10 rounded-3xl p-10 md:p-14 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
            <div className="relative">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">Need Any Registration Service?</h2>
              <p className="text-slate-400 mb-8 max-w-xl mx-auto">Get expert assistance for all your registration and certification needs.</p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="#contact-form"><Button className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6 text-lg">Request Callback<ArrowRight className="ml-2 w-5 h-5" /></Button></a>
                <Link to="/contact"><Button variant="outline" className="border-white/20 text-white hover:bg-white/10 px-8 py-6 text-lg">Contact Us</Button></Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
